import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {ArrowLeft, Shield} from 'lucide-react'

export function PrivacyPolicy() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 pb-20 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <Card className="border border-red-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl">Política de Privacidade</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Última atualização: {new Date().toLocaleDateString('pt-BR')} | Versão 1.0
            </p>
          </CardHeader>
          
          <CardContent className="prose prose-sm max-w-none">
            <h2>1. Introdução</h2>
            <p>
              Bem-vindo ao <strong>Doe</strong>, a rede que conecta quem quer ajudar com quem mais precisa. Esta Política de Privacidade explica, de forma clara e simples, como coletamos, usamos, armazenamos e protegemos seus dados pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong>.
            </p>
            <p>
              Ao utilizar nossa plataforma, você concorda com os termos descritos aqui. Se tiver qualquer dúvida, estamos à disposição para conversar.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Responsável pela plataforma:</strong><br />
              CNPJ: 63.163.098/0001-18<br />
              Luan Alysson de Souza<br />
              E-mail: luanalysson@gmail.com
            </p>

            <h2>2. Quais Dados Coletamos e Por Quê</h2>
            <p>
              Coletamos apenas o necessário para conectar doadores e pessoas que precisam de sangue. Nada além disso.
            </p>
            <h3>2.1. Dados que Você Nos Fornece</h3>
            <ul>
              <li><strong>Se você é doador:</strong> Nome completo, idade, tipo sanguíneo, cidade, estado, telefone/WhatsApp (opcional), localização GPS (somente com sua autorização)</li>
              <li><strong>Se você precisa de doação:</strong> Nome do paciente, tipo sanguíneo, hospital, cidade, estado, descrição da situação, foto do paciente (opcional), e-mail de contato, telefone/WhatsApp</li>
              <li><strong>Para sua conta:</strong> E-mail, senha (criptografada e nunca visível para nós), data de criação da conta</li>
            </ul>

            <h3>2.2. Dados Coletados Automaticamente</h3>
            <ul>
              <li><strong>Logs de Acesso:</strong> Endereço IP (anonimizado para sua privacidade), tipo de navegador, dispositivo, data e hora de acesso — usamos isso para prevenir fraudes e melhorar a plataforma</li>
              <li><strong>Cookies:</strong> Usamos apenas cookies essenciais para manter você logado e garantir que a plataforma funcione corretamente</li>
            </ul>

            <h2>3. Como Usamos Seus Dados</h2>
            <p>Seus dados existem aqui por um único motivo: <strong>salvar vidas</strong>. Usamos suas informações exclusivamente para:</p>
            <ul>
              <li><strong>Conectar quem pode ajudar com quem precisa:</strong> Fazemos matches automáticos entre doadores e solicitações baseados em tipo sanguíneo e localização. Se você é compatível com alguém próximo, você fica sabendo.</li>
              <li><strong>Avisar você quando for importante:</strong> Enviamos notificações sobre pedidos urgentes compatíveis com você, confirmações de cadastro e avisos relevantes. Sem spam, só o essencial.</li>
              <li><strong>Manter a plataforma segura:</strong> Prevenimos fraudes, identificamos abusos e protegemos a comunidade de pessoas que estão aqui com boas intenções.</li>
              <li><strong>Melhorar continuamente:</strong> Analisamos dados agregados (sem identificar você individualmente) para entender como podemos tornar a plataforma mais eficiente.</li>
              <li><strong>Cumprir a lei:</strong> Quando necessário, atendemos requisitos legais e judiciais.</li>
            </ul>

            <h2>4. Base Legal Para o Tratamento dos Seus Dados (LGPD)</h2>
            <p>Por que podemos usar seus dados? A LGPD nos permite fazer isso com base em:</p>
            <ul>
              <li><strong>Consentimento (Art. 7º, I):</strong> Você autoriza expressamente o uso dos seus dados ao aceitar esta política e criar sua conta.</li>
              <li><strong>Execução de Contrato (Art. 7º, V):</strong> Seus dados são necessários para que possamos conectar você com doadores ou solicitantes — é o serviço que a plataforma oferece.</li>
              <li><strong>Legítimo Interesse (Art. 7º, IX):</strong> Precisamos proteger a plataforma de fraudes e garantir a segurança de todos os usuários.</li>
              <li><strong>Proteção da Vida (Art. 7º, VII):</strong> Esta é a mais importante: doação de sangue pode ser literalmente questão de vida ou morte. Seus dados ajudam a salvar vidas.</li>
            </ul>

            <h2>5. Com Quem Compartilhamos Seus Dados</h2>
            <p><strong>Deixa eu ser bem direto: NÃO vendemos, alugamos ou compartilhamos seus dados com terceiros para fins comerciais. Nunca.</strong></p>
            <p>Seus dados só são compartilhados quando absolutamente necessário:</p>
            <ul>
              <li><strong>Quando você é compatível com alguém:</strong> Se houver um match entre você (doador) e uma solicitação de doação, seus dados de contato (nome, telefone, e-mail) são compartilhados com quem fez o pedido. É assim que a conexão acontece.</li>
              <li><strong>Com prestadores de serviço:</strong> Empresas que nos ajudam tecnicamente (hospedagem, envio de e-mails, análise de dados) têm acesso limitado aos dados, sempre sob contrato rígido de confidencialidade.</li>
              <li><strong>Por obrigação legal:</strong> Se a justiça ou autoridades competentes exigirem, mediante ordem judicial, precisamos cumprir a lei.</li>
            </ul>
            <p className="text-sm text-gray-600 mt-2">
              Importante: Quando você cria uma solicitação de doação, ela fica visível publicamente na plataforma para que doadores possam encontrá-la. Mas apenas informações necessárias para a doação ficam visíveis (tipo sanguíneo, hospital, cidade).
            </p>

            <h2>6. Segurança e Armazenamento dos Seus Dados</h2>
            <p>Levamos sua privacidade a sério. Aqui está o que fazemos para proteger suas informações:</p>
            <ul>
              <li><strong>Criptografia de senhas:</strong> Sua senha é criptografada com tecnologia de hash seguro (bcrypt). Nem nós conseguimos vê-la.</li>
              <li><strong>Conexão segura (HTTPS):</strong> Toda comunicação entre você e nossos servidores é criptografada. Ninguém intercepta seus dados no caminho.</li>
              <li><strong>Acesso restrito:</strong> Apenas membros autorizados da equipe têm acesso aos dados, e somente quando absolutamente necessário.</li>
              <li><strong>Backups regulares:</strong> Fazemos cópias de segurança para garantir que seus dados não se percam em caso de problemas técnicos.</li>
              <li><strong>Quanto tempo guardamos seus dados:</strong> Enquanto sua conta estiver ativa, mantemos seus dados. Se você excluir sua conta, anonimizamos seus dados sensíveis em até 30 dias. Solicitações de doação já publicadas podem permanecer visíveis (anonimizadas) para fins estatísticos.</li>
            </ul>

            <h2>7. Seus Direitos (Você Manda nos Seus Dados)</h2>
            <p>A LGPD garante que <strong>você tem controle total sobre seus dados pessoais</strong>. Você pode:</p>
            <ul>
              <li><strong>Confirmar e acessar (Art. 18, I e II):</strong> Perguntar se temos dados seus e pedir para vê-los.</li>
              <li><strong>Corrigir (Art. 18, III):</strong> Atualizar informações que estejam erradas, incompletas ou desatualizadas.</li>
              <li><strong>Anonimizar, bloquear ou deletar (Art. 18, IV):</strong> Solicitar a remoção de dados que não são mais necessários ou que foram tratados de forma inadequada.</li>
              <li><strong>Portabilidade (Art. 18, V):</strong> Receber todos os seus dados em formato estruturado (JSON) para levar para outro serviço.</li>
              <li><strong>Excluir tudo (Art. 18, VI):</strong> Deletar sua conta e todos os dados pessoais associados.</li>
              <li><strong>Revogar consentimento (Art. 18, IX):</strong> Mudar de ideia e retirar sua autorização para uso dos dados a qualquer momento.</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium">
                📧 <strong>Para exercer qualquer um desses direitos, envie um e-mail para:</strong><br />
                <a href="mailto:luanalysson@gmail.com" className="text-blue-600 hover:underline">luanalysson@gmail.com</a>
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Responderemos em até 15 dias úteis (prazo legal da LGPD).
              </p>
            </div>

            <h2>8. Sobre Dados Sensíveis (Tipo Sanguíneo)</h2>
            <p>
              <strong>Tipo sanguíneo é considerado um dado sensível de saúde pela LGPD (Art. 5º, II).</strong> Isso significa que ele precisa de uma proteção extra.
            </p>
            <p>
              Coletamos seu tipo sanguíneo <strong>apenas com seu consentimento explícito</strong> e o usamos exclusivamente para conectar doadores com quem precisa de sangue — uma finalidade legítima de <strong>proteção à vida</strong>. Seus dados de saúde não são usados para nenhum outro fim.
            </p>

            <h2>9. Menores de Idade</h2>
            <p>
              Nossa plataforma é destinada a <strong>maiores de 16 anos</strong>, que é a idade mínima para doação de sangue no Brasil. Se você tem entre 16 e 18 anos, precisa da autorização dos seus pais ou responsáveis legais para usar a plataforma.
            </p>

            <h2>10. Cookies e Tecnologias Similares</h2>
            <p>Usamos cookies (pequenos arquivos salvos no seu navegador) para melhorar sua experiência:</p>
            <ul>
              <li><strong>Cookies essenciais:</strong> Mantêm você logado e fazem a plataforma funcionar. Esses são obrigatórios.</li>
              <li><strong>Cookies funcionais:</strong> Lembram suas preferências (como localização ou configurações).</li>
              <li><strong>Cookies analíticos:</strong> Nos ajudam a entender como você usa a plataforma (via Google Analytics, sempre anonimizado — não identificamos você individualmente).</li>
            </ul>
            <p className="text-sm text-gray-600 mt-2">
              Você pode gerenciar ou desativar cookies nas configurações do seu navegador, mas alguns recursos da plataforma podem parar de funcionar.
            </p>

            <h2>11. Alterações nesta Política</h2>
            <p>
              Podemos atualizar esta política de tempos em tempos para refletir mudanças na plataforma ou na legislação. Se fizermos alterações significativas, você será avisado por e-mail ou por um aviso destacado na plataforma.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              A versão mais recente sempre estará disponível nesta página, com a data de atualização no topo.
            </p>

            <h2>12. Encarregado de Proteção de Dados (DPO)</h2>
            <p>
              Se você tiver qualquer dúvida, reclamação ou solicitação relacionada aos seus dados pessoais, pode falar diretamente com nosso Encarregado de Dados:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2">
              <p className="font-medium">Luan Alysson de Souza</p>
              <p className="text-sm text-gray-600">
                <strong>E-mail:</strong> <a href="mailto:luanalysson@gmail.com" className="text-blue-600 hover:underline">luanalysson@gmail.com</a>
              </p>
            </div>

            <h2>13. Autoridade Nacional de Proteção de Dados (ANPD)</h2>
            <p>
              Se você tiver alguma reclamação ou dúvida que não conseguimos resolver, você pode contatar a Autoridade Nacional de Proteção de Dados (ANPD), que é o órgão do governo responsável por fiscalizar a LGPD:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2">
              <p className="text-sm">
                <strong>Site:</strong> <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.gov.br/anpd</a><br />
                <strong>Endereço:</strong> SIA Trecho 3, Lote 1.350, Bloco E, Brasília-DF, CEP 71200-003
              </p>
            </div>

            <h2>14. Disposições Finais</h2>
            <p>
              Esta Política de Privacidade é regida pelas leis brasileiras, especialmente pela Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Foro competente:</strong> Comarca de Juiz de Fora/MG.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-gray-700 font-medium">
                ⚠️ <strong>Lembrete Importante:</strong> Esta plataforma conecta pessoas, mas <strong>não substitui orientação médica profissional</strong>. Em caso de emergência, procure atendimento hospitalar imediatamente. A doação de sangue deve sempre ser realizada em hemocentros ou hospitais autorizados pela Anvisa.
              </p>
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-gray-600 mb-4">
                Ao continuar usando o Doe, você confirma que leu e concorda com esta Política de Privacidade.
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Última atualização: {new Date().toLocaleDateString('pt-BR')} | Versão 2.0 | CNPJ: 63.163.098/0001-18
              </p>
              <Button onClick={() => navigate('/')}>
                Voltar à Página Inicial
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  )
}
